import { useState } from "react";

// ── Shared presentational components ──────────────────────

// Recharts tooltip styled to match the nuvportos terminal theme
export function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#111111",
      border: "1px solid #1E3018",
      padding: "10px 14px",
      fontFamily: "monospace",
      fontSize: 11,
      borderRadius: 3,
    }}>
      {(label || payload[0]?.payload?.ticker) && (
        <p style={{ color: "#3ddc84", marginBottom: 6, fontWeight: 700, letterSpacing: "0.06em" }}>
          {label || payload[0]?.payload?.ticker}
        </p>
      )}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || "#f0f0f0", lineHeight: 1.7 }}>
          {p.name}: {typeof p.value === "number" ? p.value.toFixed(2) : p.value}
          {p.unit || ""}
        </p>
      ))}
    </div>
  );
}

// A labelled key-value row used in summary panels
export function MetricRow({ label, value, valueColor = "#f0f0f0" }) {
  return (
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      padding: "6px 0",
      borderBottom: "1px solid #15201066",
    }}>
      <span style={{ color: "#888888", fontSize: 11 }}>{label}</span>
      <span style={{ color: valueColor, fontWeight: 500 }}>{value}</span>
    </div>
  );
}

// Spinning loader indicator
export function Spinner() {
  return (
    <span
      className="spin"
      style={{
        display: "inline-block",
        width: 14, height: 14,
        border: "2px solid #152010",
        borderTopColor: "#3ddc84",
        borderRadius: "50%",
      }}
    />
  );
}

// Animated shimmer skeleton bar — use for loading placeholders
export function SkeletonBar({ width = "100%", height = 12, style = {} }) {
  return (
    <div style={{
      width, height, borderRadius: 3,
      background: "linear-gradient(90deg, #1c2333 25%, #262f3d 50%, #1c2333 75%)",
      backgroundSize: "200% 100%",
      animation: "skeletonShimmer 1.5s ease-in-out infinite",
      ...style,
    }} />
  );
}

// Coloured badge: UNDERVALUED / FAIR VALUE / OVERVALUED
export function ValuationBadge({ label, color }) {
  return (
    <span style={{
      background: color + "1a",
      color,
      border: `1px solid ${color}44`,
      padding: "2px 10px",
      borderRadius: 2,
      fontSize: 9,
      letterSpacing: "0.1em",
      fontWeight: 700,
    }}>
      {label}
    </span>
  );
}

// Weight percentage bar
export function WeightBar({ value, color = "#3ddc84" }) {
  return (
    <div style={{ height: 3, background: "#1c1c1c", borderRadius: 2, overflow: "hidden" }}>
      <div style={{
        height: "100%",
        width: `${Math.min(100, value)}%`,
        background: color,
        borderRadius: 2,
        transition: "width 0.5s ease",
      }} />
    </div>
  );
}

// Section header / label
export function SectionLabel({ children, action }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 14,
    }}>
      <span style={{
        color: "#888888",
        fontSize: 9,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        fontWeight: 700,
      }}>
        {children}
      </span>
      {action}
    </div>
  );
}

// ── ModuleHeader ─────────────────────────────────────────────────────────────
export function ModuleHeader({ title, description, bullets, visible }) {
  const [expanded, setExpanded] = useState(false);
  const [btnHovered, setBtnHovered] = useState(false);

  if (!visible) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Collapsed bar */}
      <div style={{
        display:       "flex",
        alignItems:    "center",
        gap:           12,
        padding:       "10px 14px",
        background:    "var(--surface-2)",
        borderRadius:  "var(--radius-md)",
        border:        "1px solid var(--border)",
      }}>
        <span style={{
          fontSize:      10,
          letterSpacing: "0.12em",
          color:         "var(--text-secondary)",
          fontFamily:    "var(--font-mono)",
          flexShrink:    0,
        }}>
          {title}
        </span>
        <span style={{
          fontSize:   11,
          color:      "var(--text-primary)",
          flex:       1,
          fontFamily: "var(--font-body)",
        }}>
          {description}
        </span>
        <button
          onClick={() => setExpanded(v => !v)}
          style={{
            width:        28, height: 28,
            borderRadius: "50%",
            border:       `1px solid ${btnHovered ? "var(--accent)" : "var(--border)"}`,
            background:   "transparent",
            color:        btnHovered ? "var(--accent)" : "var(--text-secondary)",
            fontSize:     12,
            cursor:       "pointer",
            display:      "flex", alignItems: "center", justifyContent: "center",
            flexShrink:   0,
            transition:   "border-color 0.15s, color 0.15s",
            fontFamily:   "var(--font-mono)",
          }}
          onMouseEnter={() => setBtnHovered(true)}
          onMouseLeave={() => setBtnHovered(false)}
          title={expanded ? "Collapse" : "Learn more"}
        >
          {expanded ? "−" : "?"}
        </button>
      </div>

      {/* Expanded bullets */}
      {expanded && (
        <div style={{
          marginTop:    8,
          padding:      "10px 14px",
          background:   "var(--surface-3)",
          borderRadius: "var(--radius-sm)",
          borderLeft:   "2px solid var(--accent-mid)",
        }}>
          {bullets.map((b, i) => (
            <div key={i} style={{
              fontSize:   11,
              color:      "var(--text-secondary)",
              lineHeight: 1.7,
              fontFamily: "var(--font-body)",
            }}>
              • {b}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
